import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Ford Mustang (1967 Fastback / GT) — the long-hood/short-deck American muscle
// coupe at true scale (1 unit = 1 metre): length ~4.65, width 1.80, height 1.30,
// wheelbase 2.74 (front axle +1.15, rear -1.59), wheel radius ~0.34. Nose is +Z.
// Authored for the `cars` showcase batch. The defining silhouette is a long
// bonnet and a SWEEPING FASTBACK roof: a short flat roof panel over the cabin
// that then descends in ONE unbroken sloping line — its top edges colinear —
// all the way down to a short ducktail at the tail. There is NO flat deck behind
// the cabin (the roof line IS the rear of the car). The signature cues, all
// present: the wide rectangular Mustang grille (black mouth, chrome surround,
// central running-horse badge in a chrome corral) with round headlamps set INTO
// its outer corners and GT fog lamps flanking the corral; a long bonnet with
// twin hood scoops; muscular haunches over the rear wheels; the side "C-cove"
// with a faux scoop ahead of the rear wheel; the iconic THREE vertical tail-lamp
// bars per side in a black rear panel, a central fuel cap, chrome bumpers and
// twin exhausts; GT chrome rocker trim and bright Magnum-500-style five-spoke
// wheels. Body panels carry the "paint" recolour slot (Highland Green — the
// Bullitt colour); `drive` spins the wheels and `hazards` blinks the indicators.

const PAINT = "#2f4a34"; // Highland Green — the "paint" slot default
const TRIM = "#141518"; // gloss-black trim (grille mouth, rear panel, louvres)
const CLAD = "#202226"; // dark valances / cove insets under the bumpers
const WELL = "#141518"; // wheel-well discs
const GLASS = "#18232b"; // tinted side / rear glazing — cool, clearly not paint
const SCREEN = "#2f4656"; // windscreen (a cooler shade, lighter than the tint)
const CABIN = "#0f1113"; // near-black interior block behind the glass
const CHROME = "#ccd1d6"; // bright chrome — bumpers, surround, trim
const SILVER = "#c4c9cf"; // bright Magnum-500 spokes
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#eef4f8"; // headlamp / fog lamp glass
const RED = "#a5121f"; // tail lamp bars
const AMBER = "#e8a13a"; // indicators
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.34;
const TYRE_W_F = 0.22;
const TYRE_W_R = 0.26; // wider rear for the muscle-car stance
const WHEEL_X = 0.78; // half track — tyre outer face proud of the body side
const AXLE_F = 1.15;
const AXLE_R = -1.59; // wheelbase 2.74
const BODY_X = 0.81; // main body half-width

const SCREEN_RAKE = 0.60; // muscle-car windscreen (moderately raked, not steep)
const BONNET_TILT = 0.05; // long bonnet, front slightly lower toward the nose

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the muscular haunches and the front fender bulges. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One bright Magnum-500-style five-spoke wheel: dark tyre, brake disc, dark
 *  recessed dish, a chrome outer rim ring, five bright flat spokes proud of the
 *  tyre face, chrome centre cap. Every part shares `name` so the `drive` track
 *  spins them as one node about the axle. `side` is +1 for the right (+x) wheel,
 *  -1 for the left. */
function wheel(side: 1 | -1, tyreW: number, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const outer = WHEEL_X + tyreW / 2; // tyre outer face — must stay proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 20, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.15, 0.15, 0.03, 14, BRAKE, [side * (WHEEL_X - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.24, 0.24, 0.05, 20, DISH, [side * (outer - 0.055), y, z], [0, 0, Math.PI / 2], { name }),
    // chrome outer rim ring, proud of the tyre face
    {
      geometry: new THREE.TorusGeometry(0.29, 0.028, 6, 22),
      color: CHROME,
      position: [side * (outer + 0.006), y, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      name,
    },
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.075, 0.5, 0.09, SILVER, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.07, 0.07, 0.03, 14, CHROME, [side * (outer + 0.004), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a slim body-coloured brow (an upper half-torus over the tyre). The
 *  [0, pi/2, 0] yaw puts the 0..pi arc exactly over the top half; the tyre's
 *  outer face stays proud of both so the wheel is never swallowed. */
function arch(side: 1 | -1, z: number, discR: number, browR: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 20, WELL, [side * BODY_X, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, 0.05, 6, 20, Math.PI),
      color: PAINT,
      position: [side * (BODY_X + 0.02), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair are amber chips at the valance ends under the grille; rear pair are
// amber chips at the outer corners of the black rear panel.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.62, 0.5, 2.25], size: [0.14, 0.06, 0.04] },
  { name: "indicator-fr", pos: [0.62, 0.5, 2.25], size: [0.14, 0.06, 0.04] },
  { name: "indicator-rl", pos: [-0.8, 0.62, -2.29], size: [0.05, 0.12, 0.04] },
  { name: "indicator-rr", pos: [0.8, 0.62, -2.29], size: [0.05, 0.12, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, TYRE_W_F, AXLE_F, "wheel-fl"), ...wheel(1, TYRE_W_F, AXLE_F, "wheel-fr"),
    ...wheel(-1, TYRE_W_R, AXLE_R, "wheel-rl"), ...wheel(1, TYRE_W_R, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.45, 0.42)); // front arch
    P.push(...arch(s, AXLE_R, 0.47, 0.44)); // rear arch, slightly larger under the haunch
  }
  P.push(box(1.5, 0.14, 3.9, TRIM, [0, 0.30, -0.05])); // underbody pan

  // -- Body masses (paint) ----------------------------------------------------
  P.push(paint(1.62, 0.55, 4.0, [0, 0.565, -0.05])); // main tub, sill to belt line (~0.84)
  // Muscular rear haunches — a raised rear-quarter hip bulge rising into the
  // tail so the back half reads as muscle, not a flat van side.
  for (const s of S) P.push(blob([0.16, 0.34, 0.95], [s * 0.72, 0.64, -1.55]));
  // Softer front fender bulges over the front wheels.
  for (const s of S) P.push(blob([0.11, 0.2, 0.62], [s * 0.75, 0.62, 1.15]));

  // -- Long bonnet + fenders (paint) ------------------------------------------
  P.push(paint(1.34, 0.10, 1.55, [0, 0.87, 1.375], [BONNET_TILT, 0, 0])); // long bonnet core (cowl ~z0.6 -> nose)
  for (const s of S) P.push(paint(0.16, 0.12, 1.55, [s * 0.73, 0.865, 1.375], [BONNET_TILT, 0, 0])); // fender crowns
  P.push(paint(1.5, 0.05, 0.14, [0, 0.86, 0.6])); // cowl below the windscreen
  // Twin hood scoops — two distinct raised bumps on the long hood (GT cue), each
  // a proud body-colour bump with a dark forward-facing vent slot.
  for (const s of S) {
    P.push(paint(0.18, 0.07, 0.5, [s * 0.27, 0.915, 1.25])); // scoop bump, standing proud of the hood
    P.push(box(0.13, 0.045, 0.04, TRIM, [s * 0.27, 0.905, 1.5])); // dark forward vent slot
  }

  // -- Nose and tail backing (paint) ------------------------------------------
  P.push(paint(1.6, 0.46, 0.18, [0, 0.63, 2.15])); // nose block (grille sits on it)
  P.push(paint(1.62, 0.48, 0.16, [0, 0.62, -2.2])); // tail block (rear panel sits on it, top ~0.86)
  // Fill under the bumpers, back to the wells (no see-through in profile).
  P.push(box(1.32, 0.24, 0.3, CLAD, [0, 0.36, 2.02])); // front valance
  P.push(box(1.32, 0.24, 0.3, CLAD, [0, 0.36, -2.02])); // rear valance

  // -- The SWEEPING FASTBACK: a short flat roof panel + two descending sail
  // boxes whose top edges are COLINEAR through the breakpoints, forming ONE
  // unbroken line from the windscreen header down to a short ducktail. No step,
  // no flat deck. Breakpoints (z, y_top): roof (0.44..-0.1, ~1.20) then the
  // ramp (-0.1,1.19) -> (-1.15,1.03) -> (-2.15,0.86).
  P.push(paint(1.5, 0.10, 0.6, [0, 1.16, 0.14], [-0.04, 0, 0])); // short flat roof panel over the cabin
  P.push(paint(1.56, 0.44, 1.15, [0, 0.893, -0.625], [-0.1512, 0, 0])); // upper sail ramp
  P.push(paint(1.62, 0.46, 1.05, [0, 0.718, -1.65], [-0.1685, 0, 0])); // lower ramp onto the short ducktail
  // The fastback backlight lies ON that ramp and sweeps almost to the tail, a
  // long cool-tinted pane — the rear of the car is glass-and-roof, never a deck.
  P.push(box(1.14, 0.03, 1.8, GLASS, [0, 1.06, -1.0], [-0.155, 0, 0]));

  // -- Greenhouse -------------------------------------------------------------
  P.push(box(1.36, 0.4, 1.2, CABIN, [0, 0.86, -0.05])); // dark interior block sealing the cabin
  P.push(box(1.32, 0.44, 0.045, SCREEN, [0, 1.03, 0.5], [-SCREEN_RAKE, 0, 0])); // windscreen (well forward)
  for (const s of S) P.push(paint(0.1, 0.44, 0.08, [s * 0.68, 1.03, 0.5], [-SCREEN_RAKE, 0, 0])); // A-pillars
  for (const s of S) {
    P.push(box(0.03, 0.3, 0.7, GLASS, [s * 0.73, 0.99, 0.05], [0, 0, s * 0.1])); // door glass, tumblehome in
    P.push(paint(0.06, 0.34, 0.5, [s * 0.725, 0.95, -0.4], [0, 0, s * 0.08])); // painted C-pillar / sail flank
    P.push(box(0.02, 0.03, 0.72, CHROME, [s * 0.745, 0.84, 0.05])); // chrome belt strip under the glass
    // Close the A-pillar/cowl corner so no dark step opens where glass meets body.
    P.push(paint(0.09, 0.13, 0.12, [s * 0.7, 0.87, 0.63]));
  }
  // C-pillar sail louvres — three thin dark slats each side on the painted sail.
  for (const s of S) {
    for (let i = 0; i < 3; i++) {
      P.push(box(0.02, 0.02, 0.26, TRIM, [s * 0.79, 0.92 - i * 0.07, -0.4], [0, 0, 0]));
    }
  }

  // -- Front fascia: the wide Mustang grille with lamps set into it -----------
  P.push(box(1.6, 0.38, 0.05, CHROME, [0, 0.66, 2.22])); // chrome grille surround
  P.push(box(1.5, 0.3, 0.05, TRIM, [0, 0.66, 2.245])); // black grille mouth
  for (const yl of [0.75, 0.57]) P.push(box(1.46, 0.014, 0.02, CHROME, [0, yl, 2.27])); // fine horizontal grille bars
  // Central chrome corral with the running-horse badge (approximated).
  P.push(box(0.3, 0.2, 0.04, CHROME, [0, 0.66, 2.255])); // corral frame
  P.push(box(0.22, 0.14, 0.03, TRIM, [0, 0.66, 2.265])); // corral field
  P.push(box(0.05, 0.11, 0.03, CHROME, [0, 0.66, 2.275])); // horse badge (chrome bar)
  P.push(box(0.09, 0.02, 0.03, CHROME, [0.05, 0.63, 2.275])); // badge leg hint
  for (const s of S) {
    // Round headlamp set INTO the outer grille corner.
    P.push(cyl(0.13, 0.13, 0.03, 18, CHROME, [s * 0.62, 0.66, 2.26], [Math.PI / 2, 0, 0])); // headlamp bezel
    P.push(cyl(0.1, 0.1, 0.025, 18, LAMP, [s * 0.62, 0.66, 2.278], [Math.PI / 2, 0, 0])); // headlamp glass
    // GT fog lamp flanking the corral, inboard.
    P.push(cyl(0.085, 0.085, 0.03, 16, CHROME, [s * 0.28, 0.66, 2.26], [Math.PI / 2, 0, 0])); // fog bezel
    P.push(cyl(0.065, 0.065, 0.025, 16, LAMP, [s * 0.28, 0.66, 2.278], [Math.PI / 2, 0, 0])); // fog glass
  }
  // Chrome front bumper wrapping the corners, below the grille.
  P.push(box(1.64, 0.13, 0.14, CHROME, [0, 0.44, 2.2]));
  for (const s of S) P.push(box(0.14, 0.15, 0.16, CHROME, [s * 0.78, 0.44, 2.12])); // bumper corner wrap
  P.push(box(0.4, 0.12, 0.02, PLATE, [0, 0.44, 2.29])); // front plate on the bumper

  // -- Rear fascia: the black panel with three vertical lamp bars each side ----
  P.push(box(1.62, 0.42, 0.05, TRIM, [0, 0.62, -2.24])); // black rear panel
  for (const s of S) {
    // three DISTINCT vertical red lenses per side, proud of the black panel,
    // separated by proud black dividers so all three read clearly
    for (let i = 0; i < 3; i++) {
      const x = s * (0.3 + i * 0.18);
      P.push(box(0.12, 0.3, 0.05, RED, [x, 0.62, -2.27]));
    }
    for (let i = 0; i < 2; i++) {
      const x = s * (0.39 + i * 0.18);
      P.push(box(0.025, 0.32, 0.07, TRIM, [x, 0.62, -2.285])); // divider between the lenses
    }
  }
  // Central chrome fuel cap on the rear panel.
  P.push(cyl(0.1, 0.1, 0.04, 16, CHROME, [0, 0.66, -2.275], [Math.PI / 2, 0, 0]));
  P.push(cyl(0.045, 0.045, 0.02, 12, TRIM, [0, 0.66, -2.3], [Math.PI / 2, 0, 0])); // cap centre
  // Chrome rear bumper wrapping the corners.
  P.push(box(1.64, 0.13, 0.14, CHROME, [0, 0.4, -2.2]));
  for (const s of S) P.push(box(0.14, 0.15, 0.16, CHROME, [s * 0.78, 0.4, -2.12])); // bumper corner wrap
  P.push(box(0.4, 0.12, 0.02, PLATE, [0, 0.4, -2.29])); // rear plate on the bumper
  // Twin exhaust tips under the bumper, centre.
  for (const s of S) P.push(cyl(0.045, 0.045, 0.14, 12, CHROME, [s * 0.24, 0.26, -2.24], [Math.PI / 2, 0, 0]));

  // -- Side cove + GT rocker trim ---------------------------------------------
  for (const s of S) {
    // The C-cove: a shallow dark inset sculpted along the flank, ending in a
    // faux scoop mouth just ahead of the rear wheel.
    P.push(box(0.02, 0.14, 1.4, CLAD, [s * 0.815, 0.6, -0.4])); // cove inset panel
    P.push(box(0.04, 0.12, 0.16, TRIM, [s * 0.82, 0.6, -0.95])); // faux side-scoop mouth
    P.push(box(0.045, 0.02, 0.18, CHROME, [s * 0.825, 0.67, -0.95])); // scoop upper chrome lip
    // GT chrome rocker trim along the sill.
    P.push(box(0.03, 0.05, 2.9, CHROME, [s * 0.815, 0.36, -0.05]));
  }

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.06, 0.02, 0.03, TRIM, [s * 0.79, 0.9, 0.36])); // mirror arm
    P.push(cyl(0.045, 0.045, 0.02, 12, CHROME, [s * 0.84, 0.91, 0.36], [0, 0, Math.PI / 2])); // round chrome mirror head
    P.push(box(0.02, 0.028, 0.11, CHROME, [s * 0.815, 0.72, -0.35])); // door handle
    P.push(box(0.28, 0.012, 0.03, TRIM, [s * 0.22, 0.86, 0.68], [-SCREEN_RAKE, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const fordMustang67 = defineModel({
  id: "ford-mustang-67",
  name: "Ford Mustang (1967)",
  batchId: "cars",
  tags: ["car", "ford", "mustang", "muscle", "fastback", "classic"],
  summary: "A 1967 Ford Mustang Fastback / GT at true scale, in Highland Green.",
  description:
    "A 1967 Ford Mustang Fastback (GT) at real 1 unit = 1 metre scale (about " +
    "4.65 long, 1.80 wide, 1.30 tall, 2.74 wheelbase, on bright Magnum-500-" +
    "style five-spoke wheels). The defining silhouette is a long bonnet and a " +
    "sweeping fastback roof that flows in one continuous line from the " +
    "windscreen header down to a short ducktail, with no flat deck behind the " +
    "cabin. The signature cues are all present: the wide rectangular Mustang " +
    "grille with a chrome surround and a central running-horse badge in a " +
    "chrome corral, round headlamps set into its outer corners and GT fog " +
    "lamps flanking the corral; a long bonnet with twin hood scoops; muscular " +
    "rear haunches; the side C-cove with a faux scoop ahead of the rear wheel; " +
    "the iconic three vertical tail-lamp bars per side in a black rear panel " +
    "with a central fuel cap; chrome bumpers, twin exhausts and GT chrome " +
    "rocker trim. Body panels carry the 'paint' recolour slot (Highland " +
    "Green); `drive` spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.48,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
